import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ShoppingBag, Package, CreditCard, X, Plus, Minus, Trash2, ShoppingCart, Loader2, Truck, AlertTriangle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, getImageUrl } from '../../lib/api';
import { useCurrency } from '../../context/CurrencyContext';
import { useBasket } from '../../context/BasketContext';

const COUNTRY_REGIONS = {
  GB: 'GB', US: 'US', CA: 'CA',
  IE: 'EU', DE: 'EU', FR: 'EU', NL: 'EU', BE: 'EU', AT: 'EU', ES: 'EU', IT: 'EU', PT: 'EU', SE: 'EU', NO: 'EU', DK: 'EU', FI: 'EU', PL: 'EU', CH: 'EU', GR: 'EU',
  NG: 'AF', GH: 'AF', KE: 'AF', ZA: 'AF', TZ: 'AF', UG: 'AF', RW: 'AF', EG: 'AF',
  AU: 'OTHER', NZ: 'OTHER', IN: 'OTHER', JP: 'OTHER', KR: 'OTHER', SG: 'OTHER', BR: 'OTHER', MX: 'OTHER', AE: 'OTHER', SA: 'OTHER'
};

const ShopPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showBasket, setShowBasket] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [shippingRates, setShippingRates] = useState(null);
  const [deliveryCountry, setDeliveryCountry] = useState('GB');
  const { formatPrice } = useCurrency();
  const { basket, addToBasket, updateQuantity, removeFromBasket, clearBasket, basketCount, basketSubtotal } = useBasket();
  const [checkoutForm, setCheckoutForm] = useState({ email: '', name: '', line1: '', line2: '', city: '', postcode: '', country: 'GB' });

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/shop/products`).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/shop/categories`).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/shop/shipping-rates`).then(r => r.json()).catch(() => null)
    ]).then(([prods, cats, rates]) => {
      setProducts(prods); setCategories(cats);
      if (rates) setShippingRates(rates);
      setLoading(false);
    });
  }, []);

  const filteredProducts = activeCategory === 'all' ? products : products.filter(p => (p.category || '').toLowerCase() === activeCategory.toLowerCase());
  const bulkLimit = shippingRates?.bulk_limit || 10;
  const hasBulkOrder = basket.some(i => i.quantity >= bulkLimit);

  const getShippingCost = () => {
    if (!shippingRates?.rates) return { cost: 3.50, name: 'United Kingdom' };
    const region = COUNTRY_REGIONS[deliveryCountry] || 'OTHER';
    const rate = shippingRates.rates[region] || shippingRates.rates['OTHER'] || { cost: 14.99, name: 'International' };
    if (shippingRates.free_shipping_threshold && basketSubtotal >= shippingRates.free_shipping_threshold) {
      return { ...rate, cost: 0, freeNote: `Free delivery on orders over £${shippingRates.free_shipping_threshold}` };
    }
    return rate;
  };

  const shipping = getShippingCost();
  const basketTotal = basketSubtotal + shipping.cost;

  const handleCheckout = async () => {
    if (hasBulkOrder) { toast.error('Please contact us for bulk orders'); return; }
    if (!checkoutForm.email || !checkoutForm.email.includes('@')) { toast.error('Please enter a valid email'); return; }
    if (!checkoutForm.name || !checkoutForm.line1 || !checkoutForm.city || !checkoutForm.postcode) { toast.error('Please fill in all required fields'); return; }
    setCheckingOut(true);
    try {
      const response = await fetch(`${API_URL}/api/shop/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: basket.map(i => ({ product_id: i.product_id, type: i.type, quantity: i.quantity })),
          shipping_cost: shipping.cost, shipping_region: shipping.name,
          email: checkoutForm.email,
          shipping_address: { name: checkoutForm.name, line1: checkoutForm.line1, line2: checkoutForm.line2, city: checkoutForm.city, postcode: checkoutForm.postcode, country: checkoutForm.country },
          origin_url: window.location.origin
        })
      });
      if (response.ok) { const data = await response.json(); if (data.url) { clearBasket(); window.location.href = data.url; } }
      else { const err = await response.json(); toast.error(err.detail || 'Checkout failed'); }
    } catch (err) { toast.error('Failed to start checkout'); }
    setCheckingOut(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-emerald-50">
      <Header />
      <main className="pt-24 pb-16">
        <section className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full mb-4">
              <ShoppingBag className="w-4 h-4" /><span className="text-sm font-medium">Shop</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-800 mb-4">Creassion Shop</h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Beautiful products to nurture faith in your home. Books, resources, and more — delivered to your door.</p>
          </div>

          {categories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              <button onClick={() => setActiveCategory('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === 'all' ? 'bg-amber-500 text-white' : 'bg-white border text-slate-600'}`}>All</button>
              {categories.map(cat => (<button key={cat.slug} onClick={() => setActiveCategory(cat.name)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === cat.name ? 'bg-amber-500 text-white' : 'bg-white border text-slate-600'}`}>{cat.name}</button>))}
            </div>
          )}

          {loading ? (<div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border"><Package className="w-16 h-16 text-slate-300 mx-auto mb-4" /><h3 className="text-xl font-bold text-slate-800 mb-2">Coming Soon</h3><p className="text-slate-500">New products are on the way.</p></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => {
                const cover = getImageUrl(product.image_url);
                const price = product.price || product.physical_price || 0;
                const inBasket = basket.find(i => i.product_id === (product.product_id || product.book_id));
                return (
                  <div key={product.product_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all overflow-hidden group">
                    <div className="aspect-[3/4] relative bg-slate-100 overflow-hidden">
                      {cover ? <img src={cover} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /> : <div className="w-full h-full flex items-center justify-center"><Package className="w-16 h-16 text-slate-300" /></div>}
                      {product.category && <span className="absolute top-3 left-3 bg-white/90 text-slate-700 text-xs font-medium px-2 py-1 rounded-full">{product.category}</span>}
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-slate-800 mb-2">{product.title}</h3>
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">{product.description || ''}</p>
                      {product.author && <p className="text-sm text-slate-500 mb-4">{product.author}</p>}
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-amber-600">{formatPrice(price)}</span>
                        {inBasket ? (
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => updateQuantity(inBasket.product_id, -1)} className="h-8 w-8 p-0"><Minus className="w-4 h-4" /></Button>
                            <span className="font-bold text-slate-800 w-6 text-center">{inBasket.quantity}</span>
                            <Button size="sm" variant="outline" onClick={() => updateQuantity(inBasket.product_id, 1)} className="h-8 w-8 p-0"><Plus className="w-4 h-4" /></Button>
                          </div>
                        ) : (
                          <Button onClick={() => { addToBasket(product); toast.success(`${product.title} added to basket`); }} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-5"><ShoppingCart className="w-4 h-4 mr-2" /> Add to Basket</Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* Floating Basket Button */}
      {basketCount > 0 && !showBasket && !showCheckout && (
        <button onClick={() => setShowBasket(true)} className="fixed bottom-6 right-6 bg-amber-500 hover:bg-amber-600 text-white rounded-full px-6 py-4 shadow-2xl flex items-center gap-3 z-40 transition-transform hover:scale-105">
          <ShoppingCart className="w-5 h-5" /><span className="font-bold">{basketCount} item{basketCount > 1 ? 's' : ''}</span><span className="bg-white/20 px-3 py-1 rounded-full text-sm">{formatPrice(basketTotal)}</span>
        </button>
      )}

      {/* Basket Slide-out */}
      {showBasket && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowBasket(false)} />
          <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> Your Basket ({basketCount})</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowBasket(false)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {basket.length === 0 ? (
                <div className="text-center py-12"><ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Your basket is empty</p></div>
              ) : (<>
                {basket.map((item) => (
                  <div key={item.product_id} className="flex gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-16 h-20 bg-slate-200 rounded-lg overflow-hidden flex-shrink-0">
                      {item.image_url ? <img src={getImageUrl(item.image_url)} alt="" className="w-full h-full object-cover" /> : <Package className="w-8 h-8 text-slate-300 m-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-800 text-sm truncate">{item.title}</h4>
                      <p className="text-amber-600 font-bold text-sm">{formatPrice(item.price)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => updateQuantity(item.product_id, -1)} className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-slate-100"><Minus className="w-3 h-3" /></button>
                        <span className="font-bold text-sm w-5 text-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product_id, 1)} className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-slate-100"><Plus className="w-3 h-3" /></button>
                      </div>
                      {item.quantity >= bulkLimit && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Bulk — contact us</p>}
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button onClick={() => removeFromBasket(item.product_id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      <p className="font-bold text-slate-800 text-sm">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  </div>
                ))}
                {/* Delivery Calculator */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-3"><Truck className="w-5 h-5 text-blue-600" /><h4 className="font-bold text-blue-800 text-sm">Delivery</h4></div>
                  <select value={deliveryCountry} onChange={(e) => { setDeliveryCountry(e.target.value); setCheckoutForm(f => ({...f, country: e.target.value})); }} className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm mb-2 bg-white">
                    <option value="GB">United Kingdom</option><option value="US">United States</option><option value="CA">Canada</option>
                    <option disabled>── Europe ──</option><option value="IE">Ireland</option><option value="DE">Germany</option><option value="FR">France</option><option value="NL">Netherlands</option><option value="ES">Spain</option><option value="IT">Italy</option>
                    <option disabled>── Africa ──</option><option value="NG">Nigeria</option><option value="GH">Ghana</option><option value="KE">Kenya</option><option value="ZA">South Africa</option>
                    <option disabled>── Other ──</option><option value="AU">Australia</option><option value="NZ">New Zealand</option><option value="IN">India</option><option value="AE">UAE</option>
                  </select>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-blue-700">{shipping.name}</span>
                    <span className="font-bold text-blue-800">{shipping.cost === 0 ? 'FREE' : formatPrice(shipping.cost)}</span>
                  </div>
                  {shipping.freeNote && <p className="text-xs text-emerald-600 mt-1 font-medium">{shipping.freeNote}</p>}
                  <p className="text-xs text-blue-500 mt-1">Delivery times confirmed after order is placed.</p>
                </div>
                {hasBulkOrder && (
                  <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-amber-800">{shippingRates?.bulk_message || `For orders of ${bulkLimit}+ copies, please contact us.`}</p>
                    <a href="mailto:info@creassion.org" className="inline-flex items-center gap-1 mt-2 text-sm text-amber-700 hover:text-amber-900 underline"><Mail className="w-4 h-4" /> info@creassion.org</a>
                  </div>
                )}
              </>)}
            </div>
            {basket.length > 0 && (
              <div className="p-4 border-t space-y-2">
                <div className="flex justify-between text-sm text-slate-600"><span>Subtotal</span><span>{formatPrice(basketSubtotal)}</span></div>
                <div className="flex justify-between text-sm text-slate-600"><span>Delivery ({shipping.name})</span><span>{shipping.cost === 0 ? 'FREE' : formatPrice(shipping.cost)}</span></div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>Total</span><span>{formatPrice(basketTotal)}</span></div>
                <Button onClick={() => { if (hasBulkOrder) { toast.error('Please contact us for bulk orders'); return; } setShowBasket(false); setShowCheckout(true); }} className="w-full bg-amber-500 hover:bg-amber-600 text-white h-12 rounded-xl text-base" disabled={hasBulkOrder}><CreditCard className="w-5 h-5 mr-2" /> Proceed to Checkout</Button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-slate-800">Checkout</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCheckout(false)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="px-6 pt-4">
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                {basket.map(item => (<div key={item.product_id} className="flex justify-between text-sm"><span className="text-slate-600">{item.title} x{item.quantity}</span><span className="font-medium">{formatPrice(item.price * item.quantity)}</span></div>))}
                <div className="flex justify-between text-sm text-slate-500"><span>Delivery ({shipping.name})</span><span>{shipping.cost === 0 ? 'FREE' : formatPrice(shipping.cost)}</span></div>
                <div className="flex justify-between font-bold border-t pt-2 mt-2"><span>Total</span><span>{formatPrice(basketTotal)}</span></div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Email *</label><Input type="email" value={checkoutForm.email} onChange={(e) => setCheckoutForm({...checkoutForm, email: e.target.value})} placeholder="your@email.com" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label><Input value={checkoutForm.name} onChange={(e) => setCheckoutForm({...checkoutForm, name: e.target.value})} placeholder="Full name" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1 *</label><Input value={checkoutForm.line1} onChange={(e) => setCheckoutForm({...checkoutForm, line1: e.target.value})} placeholder="Street address" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label><Input value={checkoutForm.line2} onChange={(e) => setCheckoutForm({...checkoutForm, line2: e.target.value})} placeholder="Flat, suite, etc." /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">City *</label><Input value={checkoutForm.city} onChange={(e) => setCheckoutForm({...checkoutForm, city: e.target.value})} placeholder="City" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Postcode *</label><Input value={checkoutForm.postcode} onChange={(e) => setCheckoutForm({...checkoutForm, postcode: e.target.value})} placeholder="Postcode" /></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                <select value={checkoutForm.country} onChange={(e) => { setCheckoutForm({...checkoutForm, country: e.target.value}); setDeliveryCountry(e.target.value); }} className="w-full px-3 py-2 border rounded-lg">
                  <option value="GB">United Kingdom</option><option value="US">United States</option><option value="CA">Canada</option><option value="IE">Ireland</option><option value="DE">Germany</option><option value="FR">France</option><option value="NG">Nigeria</option><option value="GH">Ghana</option><option value="KE">Kenya</option><option value="ZA">South Africa</option><option value="AU">Australia</option><option value="NZ">New Zealand</option><option value="IN">India</option><option value="AE">UAE</option>
                </select>
              </div>
            </div>
            <div className="p-4 border-t flex gap-2">
              <Button variant="outline" onClick={() => setShowCheckout(false)} className="flex-1">Back</Button>
              <Button onClick={handleCheckout} disabled={checkingOut} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white">
                {checkingOut ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}Pay {formatPrice(basketTotal)}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ShopPage;
